import { FC, useCallback, useEffect, useMemo } from 'react';
import { FormattedMessage } from 'react-intl';
import Card from 'components/UI/Card';
import { CardHeaderTop, CardHeaderTopColumn } from 'pages/Connectivity/styled';
import Heading, { Type as HeadingType } from 'components/UI/Heading';
import { useAppDispatch, useAppSelector } from 'hooks/redux';
import { ConnectivityDetails, DeviceErrorFilterAttribute } from 'models';
import {
  connectivityDetailsSelector,
  fetchDeviceErrors,
} from 'store/connectivityState/connectivityState';
import Loader from 'components/UI/Loader';
import DeviceErrorTree from '../DeviceErrorTree';
import InputField from 'components/UI/InputField';
import { useForm } from 'react-hook-form';
import { format } from 'date-fns';
import Button from 'components/UI/Button';
import { ButtonGrid, ThreeColumnGrid } from './styled';

type Props = {
  id: string;
  startTime: string;
  endTime: string;
};
const DeviceErrorCard: FC<Props> = ({ id, startTime, endTime }) => {
  const dispatch = useAppDispatch();
  const {
    register,
    watch,
    setValue,
    formState: { errors },
  } = useForm();
  const { deviceErrors }: ConnectivityDetails = useAppSelector(
    connectivityDetailsSelector
  );
  const [start, end] = watch([
    DeviceErrorFilterAttribute.Start,
    DeviceErrorFilterAttribute.End,
  ]);

  useEffect(() => {
    if (id) {
      dispatch(fetchDeviceErrors(id, startTime, endTime));
    }
  }, [dispatch, id, startTime, endTime]);

  const onClick = useCallback(() => {
    dispatch(fetchDeviceErrors(id, start, end));
  }, [dispatch, id, start, end]);

  const setDate = useCallback(
    (name: DeviceErrorFilterAttribute) => (date: Date) => {
      setValue(name, format(date, 'yyyy-MM-dd'), {
        shouldDirty: true,
      });
    },
    [setValue]
  );

  const errorResult = useMemo(() => {
    if (deviceErrors.isLoading) {
      return <Loader center />;
    }
    if (deviceErrors.error) {
      return (
        <Heading type={HeadingType.h4}>{deviceErrors.error.title}</Heading>
      );
    }
    if (!deviceErrors.data || deviceErrors.data.length === 0) {
      return <Heading type={HeadingType.h4}>No errors</Heading>;
    }
    return <DeviceErrorTree deviceErrors={deviceErrors.data} />;
  }, [deviceErrors]);

  return (
    <Card>
      <CardHeaderTop>
        <CardHeaderTopColumn>
          <Heading type={HeadingType.h3} uppercase>
            <FormattedMessage id="connectivity.device_errors" />
          </Heading>
        </CardHeaderTopColumn>
      </CardHeaderTop>
      <ThreeColumnGrid>
        <InputField
          minWidth={50}
          tight={true}
          type="date"
          description="connectivity.device_errors_start"
          defaultValue={startTime}
          register={register(DeviceErrorFilterAttribute.Start)}
          setDate={setDate(DeviceErrorFilterAttribute.Start)}
          error={errors.start}
        />
        <InputField
          minWidth={50}
          type="date"
          tight={true}
          description="connectivity.device_errors_end"
          register={register(DeviceErrorFilterAttribute.End)}
          setDate={setDate(DeviceErrorFilterAttribute.End)}
          defaultValue={endTime}
          error={errors.end}
        />
        <ButtonGrid>
          <Button id="error_search_btn" onClick={onClick}>
            <FormattedMessage id="search.btn" />
          </Button>
        </ButtonGrid>
      </ThreeColumnGrid>
      {errorResult}
    </Card>
  );
};

export default DeviceErrorCard;
